import { useCallback, useContext, useMemo } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { DataContext } from '../context/DataContext';
import { WHISPERBI_SECURITY_API } from '../env';

const API_BASE_URL = WHISPERBI_SECURITY_API;
const USERS_URL = `${API_BASE_URL}/useraccount/users`;
const ACCOUNT_SECURITY_URL = `${API_BASE_URL}/useraccount/security`;

async function getIdToken() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    return tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('SecurityUsersService.getIdToken: unable to fetch auth session', error);
    return null;
  }
}

async function request(url, options = {}, { requireAuth = true } = {}) {
  const init = { ...options };
  const headers = new Headers(init.headers || {});

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (requireAuth) {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No se encontró un token de autenticación para realizar la operación solicitada.');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  init.headers = headers;
  init.mode = init.mode || 'cors';
  init.cache = init.cache || 'no-cache';

  let response;
  try {
    response = await fetch(url, init);
  } catch (networkError) {
    console.error('SecurityUsersService.request: network error', networkError);
    throw new Error('No se pudo conectar con el servicio de usuarios. Verifica tu conexión o intenta más tarde.');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      const backendMessage = data?.message || data?.error || data?.errorMessage;
      if (backendMessage) {
        errorMessage = backendMessage;
      }
    } catch {
      const text = await response.text().catch(() => '');
      if (text) {
        errorMessage = text;
      }
    }
    throw new Error(errorMessage || 'Error inesperado al comunicar con el servicio de usuarios.');
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

// Devuelve el body completo del backend: {items, count, mfa_coverage}
// -- ver list_account_users() en security-roles/app.py. Antes SecurityUsers.jsx
// trataba el resultado como si fuera directamente el array de usuarios
// (Array.isArray(response) ? response : []), lo cual siempre daba false y
// dejaba la tabla vacía en silencio -- se corrige en el propio consumidor.
export async function listUsers() {
  return request(USERS_URL, { method: 'GET' });
}

export async function updateAccountSecurity({ mfaRequired }) {
  return request(ACCOUNT_SECURITY_URL, {
    method: 'PUT',
    body: JSON.stringify({ mfa_required: Boolean(mfaRequired) })
  });
}

// Un usuario puede tener varios roles a la vez (role_ids, lista) --
// reemplaza la lista completa, mismo criterio de "set" simple que ya usaba
// el antiguo updateUserRole escalar (ver update_user_roles en
// security-roles/app.py).
export async function updateUserRoles(userId, roleIds) {
  if (!userId) {
    throw new Error('Se requiere el identificador del usuario para reasignar sus roles.');
  }
  if (!Array.isArray(roleIds)) {
    throw new Error('Se requiere la lista de roles a asignar.');
  }

  const url = `${USERS_URL}/${encodeURIComponent(userId)}/roles`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify({ role_ids: roleIds })
  });
}

// Un usuario puede pertenecer a varios Grupos a la vez -- mismo criterio de
// reemplazo completo que updateUserRoles.
export async function updateUserGroups(userId, groupIds) {
  if (!userId) {
    throw new Error('Se requiere el identificador del usuario para reasignar sus grupos.');
  }
  if (!Array.isArray(groupIds)) {
    throw new Error('Se requiere la lista de grupos a asignar.');
  }

  const url = `${USERS_URL}/${encodeURIComponent(userId)}/groups`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify({ group_ids: groupIds })
  });
}

function ensureRoleManagerAccess(isPlatformRoot, isTenantAdmin) {
  if (!isPlatformRoot && !isTenantAdmin) {
    throw new Error('Solo administradores de la cuenta pueden realizar esta operación.');
  }
}

export const useSecurityUsersService = () => {
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const guard = useCallback(() => {
    ensureRoleManagerAccess(isPlatformRoot, isTenantAdmin);
  }, [isPlatformRoot, isTenantAdmin]);

  const guardedActions = useMemo(() => ({
    updateUserRoles: async (userId, roleIds) => {
      guard();
      return updateUserRoles(userId, roleIds);
    },
    updateUserGroups: async (userId, groupIds) => {
      guard();
      return updateUserGroups(userId, groupIds);
    },
    updateAccountSecurity: async ({ mfaRequired }) => {
      guard();
      return updateAccountSecurity({ mfaRequired });
    }
  }), [guard]);

  return {
    ...guardedActions,
    // Solo lectura: cualquier usuario autenticado puede consultar el listado.
    listUsers
  };
};

const SecurityUsersService = {
  listUsers,
  updateUserRoles,
  updateUserGroups,
  updateAccountSecurity,
  useSecurityUsersService
};

export default SecurityUsersService;
